import React, { useState, useEffect, FormEvent } from 'react';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { isAtLeast } from '../utils/roles';
import { usePageAuthorization } from '../hooks/usePageAuthorization';

interface SubCommitteeMember {
  userId: string;
  role: string;
  user: {
    id: string;
    fullName: string;
    email: string;
    role: string;
  };
}

interface SubCommittee {
  id: string;
  name: string;
  description: string;
  members: SubCommitteeMember[];
  _count?: { members: number; tasks: number; memberAffiliations: number };
}

interface Task {
  id: string;
  title: string;
  description: string;
  status: 'ToDo' | 'InProgress' | 'Done';
  dueDate: string;
  isOverdue: boolean;
  subcommitteeId: string;
  subcommittee?: { name: string };
}

interface MasterPlan {
  completionPercentage: number;
  tasks: Task[];
}

type StatusFilter = 'All' | 'ToDo' | 'InProgress' | 'Done';

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '2rem', maxWidth: '1200px', margin: '0 auto' },
  title: { color: '#1a6b3c', fontSize: '1.75rem', margin: '0 0 1.5rem 0' },
  section: { marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  sectionTitle: { color: '#0f2d1f', fontSize: '1.2rem', marginBottom: '1rem', borderBottom: '2px solid #c9a84c', paddingBottom: '0.25rem' },
  btn: { padding: '0.5rem 1rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', marginRight: '0.5rem' },
  btnGold: { padding: '0.4rem 0.75rem', backgroundColor: '#c9a84c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', marginRight: '0.25rem' },
  btnSmall: { padding: '0.3rem 0.6rem', border: 'none', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', marginRight: '0.25rem' },
  input: { padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' as const },
  select: { padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', backgroundColor: '#fff' },
  taskCard: { padding: '1rem', border: '1px solid #e0e0e0', borderRadius: '6px', marginBottom: '0.75rem', backgroundColor: '#fafafa' },
  taskTitle: { fontWeight: 600, color: '#0f2d1f', marginBottom: '0.25rem' },
  taskMeta: { fontSize: '0.8rem', color: '#666', marginBottom: '0.5rem' },
  badge: { display: 'inline-block', padding: '0.2rem 0.5rem', borderRadius: '3px', fontSize: '0.7rem', fontWeight: 600, marginRight: '0.5rem' },
  committeeCard: { display: 'inline-block', padding: '0.75rem 1.25rem', backgroundColor: '#f5f5f5', borderRadius: '6px', marginRight: '0.75rem', marginBottom: '0.75rem' },
  progressBar: { width: '100%', height: '24px', backgroundColor: '#e0e0e0', borderRadius: '12px', overflow: 'hidden', marginBottom: '1rem' },
  progressFill: { height: '100%', backgroundColor: '#1a6b3c', borderRadius: '12px', transition: 'width 0.3s', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.8rem', fontWeight: 600 },
  filterBar: { display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' as const, alignItems: 'center' },
  error: { color: '#d32f2f', backgroundColor: '#fdecea', padding: '0.5rem 0.75rem', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.85rem' },
  field: { marginBottom: '1rem' },
  label: { display: 'block', marginBottom: '0.25rem', fontWeight: 600, fontSize: '0.85rem', color: '#333' },
  toggleActive: { padding: '0.4rem 0.75rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', marginLeft: '0.5rem' },
  toggleInactive: { padding: '0.4rem 0.75rem', backgroundColor: '#e0e0e0', color: '#333', border: 'none', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', marginLeft: '0.5rem' },
  tableWrapper: { overflowX: 'auto' as const },
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '0.9rem' },
  th: { textAlign: 'left' as const, padding: '0.75rem', borderBottom: '2px solid #e0e0e0', fontWeight: 600, color: '#0f2d1f', fontSize: '0.85rem' },
  td: { padding: '0.75rem', borderBottom: '1px solid #e0e0e0', verticalAlign: 'top' as const, color: '#333' },
  editBtn: { padding: '0.3rem 0.6rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', outline: 'revert' },
  memberChair: { fontWeight: 700, color: '#0f2d1f' },
  memberName: { fontWeight: 400, color: '#333' },
};

const statusColors: Record<string, string> = {
  ToDo: '#666',
  InProgress: '#c9a84c',
  Done: '#1a6b3c',
};

export function truncateDescription(desc: string | null | undefined, maxLength = 100): string {
  if (!desc || desc.length === 0) return '–';
  if (desc.length <= maxLength) return desc;
  return desc.slice(0, maxLength) + '…';
}

export function formatMembersForTable(members: SubCommitteeMember[]): { displayed: SubCommitteeMember[]; remaining: number } {
  const sorted = [...members].sort((a, b) => {
    if (a.role === 'Chair' && b.role !== 'Chair') return -1;
    if (b.role === 'Chair' && a.role !== 'Chair') return 1;
    return a.user.fullName.localeCompare(b.user.fullName);
  });

  return {
    displayed: sorted.slice(0, 3),
    remaining: Math.max(0, sorted.length - 3),
  };
}

export function TasksPage() {
  const { user } = useAuth();
  const { canEdit } = usePageAuthorization('tasks');
  const isOrganizer = isAtLeast(user?.role || '', 'Organizer');
  const [viewMode, setViewMode] = useState<'pills' | 'table'>('pills');
  const [subcommittees, setSubcommittees] = useState<SubCommittee[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [masterPlan, setMasterPlan] = useState<MasterPlan | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('All');
  const [committeeFilter, setCommitteeFilter] = useState('All');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showMasterPlan, setShowMasterPlan] = useState(false);
  const [formTitle, setFormTitle] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formCommittee, setFormCommittee] = useState('');
  const [formDueDate, setFormDueDate] = useState('');
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingCommittee, setEditingCommittee] = useState<SubCommittee | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editError, setEditError] = useState('');
  const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null);
  const [sectionsCollapsed, setSectionsCollapsed] = useState<Record<string, boolean>>({
    subcommittees: false,
    tasks: false,
    masterPlan: false,
  });

  function toggleSection(section: string) {
    setSectionsCollapsed((prev) => ({ ...prev, [section]: !prev[section] }));
  }

  async function fetchData() {
    try {
      const [comms, taskList] = await Promise.all([
        apiClient<SubCommittee[]>('/subcommittees'),
        apiClient<Task[]>('/tasks'),
      ]);
      setSubcommittees(comms);
      setTasks(taskList);
    } catch {
      setError('Failed to load data.');
    }
  }

  async function fetchMasterPlan() {
    try {
      const plan = await apiClient<MasterPlan>('/tasks/master-plan');
      setMasterPlan(plan);
      setShowMasterPlan(true);
    } catch {
      setError('Failed to load Master Plan.');
    }
  }

  useEffect(() => { fetchData(); }, []);

  async function handleCreateTask(e: FormEvent) {
    e.preventDefault();
    if (!formTitle || !formCommittee || !formDueDate) {
      setError('Title, sub-committee, and due date are required.');
      return;
    }
    try {
      await apiClient('/tasks', {
        method: 'POST',
        body: { title: formTitle, description: formDesc, subcommitteeId: formCommittee, dueDate: formDueDate },
      });
      setFormTitle(''); setFormDesc(''); setFormCommittee(''); setFormDueDate('');
      setShowCreateForm(false);
      fetchData();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to create task.');
    }
  }

  async function handleStatusUpdate(taskId: string, newStatus: 'ToDo' | 'InProgress' | 'Done') {
    try {
      await apiClient(`/tasks/${taskId}`, { method: 'PATCH', body: { status: newStatus } });
      fetchData();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to update task status.');
    }
  }

  async function handleExportCsv() {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || (typeof window !== 'undefined' ? window.location.origin + '/api' : 'http://localhost:3000')}/tasks/master-plan/export?format=csv`,
        { headers: { Authorization: `Bearer ${document.cookie}` } }
      );
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'master-plan.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Failed to export CSV.');
    }
  }

  function openEditModal(sc: SubCommittee) {
    setEditingCommittee(sc);
    setEditName(sc.name);
    setEditDescription(sc.description);
    setEditError('');
    setExpandedId(expandedId === sc.id ? null : sc.id);
  }

  async function handleSaveCommittee() {
    if (!editingCommittee) return;
    try {
      await apiClient(`/subcommittees/${editingCommittee.id}`, {
        method: 'PATCH',
        body: { name: editName, description: editDescription },
      });
      setEditingCommittee(null);
      setEditError('');
      fetchData();
    } catch (err: unknown) {
      const apiErr = err as { statusCode?: number; message?: string | string[] };
      if (apiErr.statusCode === 409) {
        setEditError('Committee name already in use.');
      } else if (apiErr.statusCode === 400) {
        const msg = Array.isArray(apiErr.message) ? apiErr.message.join(', ') : (apiErr.message || 'Validation error.');
        setEditError(msg);
      } else {
        setEditError('Failed to update committee.');
      }
    }
  }

  const filteredTasks = tasks.filter((t) => {
    if (statusFilter !== 'All' && t.status !== statusFilter) return false;
    if (committeeFilter !== 'All' && t.subcommitteeId !== committeeFilter) return false;
    return true;
  });

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>Committee Tasks</h1>
      {error && <div style={styles.error}>{error}</div>}

      {/* Sub-committees */}
      <div style={styles.section}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: sectionsCollapsed.subcommittees ? 0 : '1rem', cursor: 'pointer' }} onClick={() => toggleSection('subcommittees')}>
          <span style={{ fontSize: '0.9rem', color: '#888', marginRight: '0.5rem' }}>{sectionsCollapsed.subcommittees ? '▶' : '▼'}</span>
          <h2 style={{ ...styles.sectionTitle, marginBottom: 0, borderBottom: '2px solid #c9a84c', paddingBottom: '0.25rem', flex: 1 }}>Sub-Committees</h2>
          {!sectionsCollapsed.subcommittees && (
            <>
              <button
                style={viewMode === 'pills' ? styles.toggleActive : styles.toggleInactive}
                onClick={(e) => { e.stopPropagation(); setViewMode('pills'); }}
              >
                Cards
              </button>
              <button
                style={viewMode === 'table' ? styles.toggleActive : styles.toggleInactive}
                onClick={(e) => { e.stopPropagation(); setViewMode('table'); }}
              >
                Table
              </button>
            </>
          )}
        </div>
        {!sectionsCollapsed.subcommittees && (
        <>
        {viewMode === 'pills' ? (
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {subcommittees.map((sc) => (
              <div
                key={sc.id}
                style={{ ...styles.committeeCard, cursor: 'pointer', verticalAlign: 'top' }}
                onClick={() => openEditModal(sc)}
              >
                <div style={{ fontWeight: 600, color: '#0f2d1f' }}>{sc.name}</div>
                <div style={{ fontSize: '0.8rem', color: '#666' }}>{sc._count?.memberAffiliations ?? sc.members.length} members · {sc._count?.tasks ?? 0} tasks</div>
                {expandedId === sc.id && (
                  <div style={{ marginTop: '0.5rem', borderTop: '1px solid #e0e0e0', paddingTop: '0.5rem' }}>
                    {sc.description && (
                      <p style={{ fontSize: '0.8rem', color: '#555', margin: '0 0 0.5rem 0' }}>{sc.description}</p>
                    )}
                    <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.5rem' }}>
                      {sc._count?.memberAffiliations ?? sc.members.length} members · {sc._count?.tasks ?? 0} tasks
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th scope="col" style={styles.th}>Name</th>
                  <th scope="col" style={styles.th}>Description</th>
                  <th scope="col" style={styles.th}>Members</th>
                  <th scope="col" style={styles.th}>Task Count</th>
                  {canEdit && isOrganizer && <th scope="col" style={styles.th}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {[...subcommittees].sort((a, b) => a.name.localeCompare(b.name)).map((sc) => {
                  const memberCount = sc._count?.memberAffiliations ?? sc.members.length;
                  return (
                    <tr key={sc.id}>
                      <td style={styles.td}>{sc.name}</td>
                      <td style={styles.td}>{truncateDescription(sc.description)}</td>
                      <td style={styles.td}>
                        {memberCount === 0 ? (
                          <span style={{ color: '#999' }}>No members</span>
                        ) : (
                          <span style={{ fontWeight: 500 }}>{memberCount} member{memberCount !== 1 ? 's' : ''}</span>
                        )}
                      </td>
                      <td style={styles.td}>{sc._count?.tasks ?? 0}</td>
                      {canEdit && isOrganizer && (
                        <td style={styles.td}>
                          <button
                            aria-label={`Edit ${sc.name}`}
                            style={styles.editBtn}
                            onClick={() => openEditModal(sc)}
                          >
                            Edit
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        </>
        )}
      </div>

      {/* Task List */}
      <div style={styles.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: sectionsCollapsed.tasks ? 0 : '1rem', cursor: 'pointer' }} onClick={() => toggleSection('tasks')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.9rem', color: '#888' }}>{sectionsCollapsed.tasks ? '▶' : '▼'}</span>
            <h2 style={{ ...styles.sectionTitle, marginBottom: 0, borderBottom: 'none', paddingBottom: 0 }}>Tasks</h2>
          </div>
          {!sectionsCollapsed.tasks && (
            <div onClick={(e) => e.stopPropagation()}>
              {canEdit && <button style={styles.btn} onClick={() => setShowCreateForm(!showCreateForm)}>+ New Task</button>}
              <button style={styles.btnGold} onClick={fetchMasterPlan}>Master Plan</button>
            </div>
          )}
        </div>
        {!sectionsCollapsed.tasks && (
        <>
        {/* Filters */}
        <div style={styles.filterBar}>
          <select style={styles.select} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}>
            <option value="All">All Statuses</option>
            <option value="ToDo">To Do</option>
            <option value="InProgress">In Progress</option>
            <option value="Done">Done</option>
          </select>
          <select style={styles.select} value={committeeFilter} onChange={(e) => setCommitteeFilter(e.target.value)}>
            <option value="All">All Committees</option>
            {subcommittees.map((sc) => (
              <option key={sc.id} value={sc.id}>{sc.name}</option>
            ))}
          </select>
        </div>

        {/* Create Task Form */}
        {showCreateForm && (
          <form onSubmit={handleCreateTask} style={{ padding: '1rem', backgroundColor: '#f9f9f9', borderRadius: '6px', marginBottom: '1rem' }}>
            <div style={styles.field}>
              <label style={styles.label}>Title</label>
              <input style={{ ...styles.input, width: '100%' }} value={formTitle} onChange={(e) => setFormTitle(e.target.value)} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Description</label>
              <textarea style={{ ...styles.input, width: '100%', minHeight: '60px' }} value={formDesc} onChange={(e) => setFormDesc(e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <div style={{ ...styles.field, flex: 1 }}>
                <label style={styles.label}>Sub-Committee</label>
                <select style={{ ...styles.select, width: '100%' }} value={formCommittee} onChange={(e) => setFormCommittee(e.target.value)}>
                  <option value="">Select...</option>
                  {subcommittees.map((sc) => <option key={sc.id} value={sc.id}>{sc.name}</option>)}
                </select>
              </div>
              <div style={{ ...styles.field, flex: 1 }}>
                <label style={styles.label}>Due Date</label>
                <input type="date" style={{ ...styles.input, width: '100%' }} value={formDueDate} onChange={(e) => setFormDueDate(e.target.value)} />
              </div>
            </div>
            <button type="submit" style={styles.btn}>Create Task</button>
          </form>
        )}

        {/* Task Cards */}
        {filteredTasks.length === 0 ? (
          <p style={{ color: '#666' }}>No tasks match the current filters.</p>
        ) : (
          filteredTasks.map((task) => {
            const isTaskExpanded = expandedTaskId === task.id;
            return (
              <div
                key={task.id}
                style={{ ...styles.taskCard, borderLeft: `4px solid ${statusColors[task.status]}`, cursor: 'pointer' }}
                onClick={() => setExpandedTaskId(isTaskExpanded ? null : task.id)}
                role="button"
                aria-expanded={isTaskExpanded}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setExpandedTaskId(isTaskExpanded ? null : task.id);
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                    <span style={{ fontSize: '0.8rem', color: '#888' }}>{isTaskExpanded ? '▼' : '▶'}</span>
                    <span style={styles.taskTitle}>
                      {task.title}
                      {task.isOverdue && <span style={{ ...styles.badge, backgroundColor: '#d32f2f', color: '#fff', marginLeft: '0.5rem' }}>OVERDUE</span>}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
                    <span style={{ ...styles.badge, backgroundColor: statusColors[task.status], color: '#fff' }}>{task.status}</span>
                    <span style={{ fontSize: '0.8rem', color: '#666' }}>{task.subcommittee?.name || 'Unassigned'}</span>
                    <span style={{ fontSize: '0.8rem', color: '#666' }}>Due: {new Date(task.dueDate).toLocaleDateString()}</span>
                  </div>
                </div>
                {isTaskExpanded && (
                  <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e0e0e0' }} onClick={(e) => e.stopPropagation()}>
                    {task.description && <p style={{ fontSize: '0.85rem', color: '#444', margin: '0 0 0.75rem 0' }}>{task.description}</p>}
                    <div>
                      {canEdit && task.status !== 'ToDo' && <button style={{ ...styles.btnSmall, backgroundColor: '#666', color: '#fff' }} onClick={() => handleStatusUpdate(task.id, 'ToDo')}>To Do</button>}
                      {canEdit && task.status !== 'InProgress' && <button style={{ ...styles.btnSmall, backgroundColor: '#c9a84c', color: '#fff' }} onClick={() => handleStatusUpdate(task.id, 'InProgress')}>In Progress</button>}
                      {canEdit && task.status !== 'Done' && <button style={{ ...styles.btnSmall, backgroundColor: '#1a6b3c', color: '#fff' }} onClick={() => handleStatusUpdate(task.id, 'Done')}>Done</button>}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
        </>
        )}
      </div>

      {/* Master Plan View */}
      {showMasterPlan && masterPlan && (
        <div style={styles.section}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => toggleSection('masterPlan')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.9rem', color: '#888' }}>{sectionsCollapsed.masterPlan ? '▶' : '▼'}</span>
              <h2 style={styles.sectionTitle}>Master Plan</h2>
            </div>
            {!sectionsCollapsed.masterPlan && (
              <button style={styles.btnGold} onClick={(e) => { e.stopPropagation(); handleExportCsv(); }}>Export CSV</button>
            )}
          </div>
          {!sectionsCollapsed.masterPlan && (
          <>
          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, width: `${masterPlan.completionPercentage}%` }}>
              {masterPlan.completionPercentage.toFixed(0)}%
            </div>
          </div>
          {masterPlan.tasks
            .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
            .map((task) => (
              <div key={task.id} style={{ ...styles.taskCard, borderLeft: `4px solid ${statusColors[task.status]}` }}>
                <div style={styles.taskTitle}>{task.title}</div>
                <div style={styles.taskMeta}>
                  <span style={{ ...styles.badge, backgroundColor: statusColors[task.status], color: '#fff' }}>{task.status}</span>
                  {task.subcommittee?.name} · Due: {new Date(task.dueDate).toLocaleDateString()}
                  {task.isOverdue && <span style={{ ...styles.badge, backgroundColor: '#d32f2f', color: '#fff', marginLeft: '0.5rem' }}>OVERDUE</span>}
                </div>
              </div>
            ))}
          </>
          )}
        </div>
      )}
      {/* Edit Committee Modal */}
      {editingCommittee && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '2rem', width: '500px', maxWidth: '90vw', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
            <h2 style={{ color: '#0f2d1f', fontSize: '1.2rem', marginTop: 0, marginBottom: '1rem' }}>Edit Committee</h2>
            {editError && <div style={styles.error}>{editError}</div>}
            <div style={styles.field}>
              <label style={styles.label}>Name</label>
              <input
                style={{ ...styles.input, width: '100%' }}
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                maxLength={100}
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Description</label>
              <textarea
                style={{ ...styles.input, width: '100%', minHeight: '80px', resize: 'vertical' } as React.CSSProperties}
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                maxLength={500}
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Members</label>
              <div style={{ fontSize: '0.85rem', color: '#333' }}>
                {editingCommittee.members.map((m) => (
                  <div key={m.userId} style={{ marginBottom: '0.25rem' }}>
                    <span style={{ fontWeight: m.role === 'Chair' ? 700 : 400 }}>{m.user.fullName}</span>
                    <span style={{ color: '#888', marginLeft: '0.5rem' }}>{m.role}</span>
                  </div>
                ))}
                {editingCommittee.members.length === 0 && <span style={{ color: '#999' }}>No members</span>}
              </div>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Tasks</label>
              <span style={{ fontSize: '0.85rem', color: '#666' }}>{editingCommittee._count?.tasks ?? 0} tasks</span>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
              <button style={styles.btn} onClick={handleSaveCommittee}>Save</button>
              <button style={{ ...styles.btn, backgroundColor: '#666' }} onClick={() => { setEditingCommittee(null); setEditError(''); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
